<script>
import SampleExtensionTableDTO from "@/DTO/ExtensionTables/SampleExtensionTableDTO";
import SubmissionExtensionTableDTO from "@/DTO/ExtensionTables/SubmissionExtensionTableDTO";
import SampleCollection from "@/collections/lab/SampleCollection";
import SubmissionCollection from "@/collections/lab/SubmissionCollection";
import { Collection } from "vue-mc";

export default {
  name: "BaseServerTable",
  props: {
    collection: {
      type: Object,
      default: () => null,
    },
    show_columns: {
      type: Array,
      default: () => [],
    },
    filterable: {
      type: Array,
      default: () => [],
    },
    sortable: {
      type: Array,
      default: () => [],
    },
    skip_filter_reset: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      table_config: {
        options: {
          requestFunction: this.requestFunction,
          responseAdapter: this.responseAdapter,
        },
      },
      useVuex: false,
      theme: "bootstrap4",
      template: "default",
      ref: `table-${this.$uuid.v1()}`,
      customFilters: {},
      loading: false,
    };
  },
  methods: {
    getDefaultTableConfig(actions_access = true) {
      const columns = actions_access
        ? [...this.show_columns]
        : [...this.show_columns.filter((column) => column !== "actions")];

      return {
        columns,
        options: {
          debounce: 800,
          sortable: this.sortable,
          filterable: this.filterable,
          sortIcon: {
            base: "fa",
            up: "fa-sort-asc",
            down: "fa-sort-desc",
            is: "fa-sort",
          },
          texts: {
            filter: "Search:",
            filterPlaceholder: "",
            limit: "Per Page:",
          },
          pagination: {
            chunk: 5,
            edge: false,
            nav: "scroll",
          },
        },
      };
    },
    isCollectionSet() {
      return this.collection instanceof Collection;
    },
    showColumn(key) {
      return this.show_columns.indexOf(key) !== -1;
    },
    toggleColumn(columnKey, show) {
      const index = this.table_config.columns.indexOf(columnKey)

      if (show && index === -1) {
        const orignalIndex = this.show_columns.indexOf(columnKey)
        if (orignalIndex !== -1) {
          this.table_config.columns.splice(orignalIndex, 0, columnKey)
        } else {
          this.table_config.columns.push(columnKey)
        }
      } else if (!show && index !== -1) {
        this.table_config.columns.splice(index, 1)
      }

      this.$refs[this.ref] && this.$refs[this.ref].refresh()
    },
    requestFunction(data) {
      this.applyFilterParams(data);

      this.loading = true;

      return new Promise((resolve, reject) => {
        this.collection.fetch().then((response) => {
          this.loading = false;

          if (response && response.getStatus() === 200) {
            this.onFetchSuccess(this.collection);

            this.checkForMerge();

            resolve(this.collection);
          } else {
            this.onFetchError("Collection could not be fetched");

            reject();
          }
        });
      });
    },
    checkForMerge() {
      // if the collection is a submission we need to merge in the submission type details data
      if (this.collection instanceof SubmissionCollection) {
        this.submissionModelMerge();
      }
      if (this.collection instanceof SampleCollection) {
        this.sampleModelMerge();
      }
    },
    sampleModelMerge() {
      this.mergeUpdateFields(SampleExtensionTableDTO.run(this.collection.models));
    },
    submissionModelMerge() {
      this.mergeUpdateFields(SubmissionExtensionTableDTO.run(this.collection.models))
    },
    mergeUpdateFields(updatedFields) {
      this.collection.models.forEach((model, idx) => {
        if (typeof updatedFields[idx] === 'object') {
          model.set(updatedFields[idx].fields);
        }
      });
    },
    applyFilterParams(data) {
      const Filter = this.collection.getFilter();

      if (!this.skip_filter_reset) {
        Filter.resetSort();
        Filter.resetDefaultSort();
        Filter.resetFilters();
        Filter.resetSearch();
      }

      this.prepareCollectionFilter(Filter);

      if (data.orderBy) {
        Filter.sortAdd(data.orderBy, data.ascending ? "ASC" : "DESC");
      }

      if (data.query && this.sortable) {
        Filter.searchAdd(data.query);
      }

      if (this.customFilters) {
        for (let filterKey in this.customFilters) {
          const filter = this.customFilters[filterKey];

          Filter.filterAdd(
            filter.field,
            filter.operator,
            filter.value,
            filter.logic
          );
        }
      }

      Filter.setLimit(data.limit);
      Filter.setPage(data.page);
    },
    prepareCollectionFilter(CollectionFilter) {
      return CollectionFilter;
    },
    responseAdapter(collection) {
      if (collection) {
        return {
          data: collection.models,
          count: collection.get("count"),
        };
      }

      return null;
    },
    setCustomFilter(key, filter) {
      if (filter && filter.value && filter.value !== "") {
        this.customFilters[key] = filter;
      } else if (this.customFilters[key]) {
        delete this.customFilters[key];
      }
      this.$refs[this.ref].refresh();
    },
    triggerModelDelete(data) {
      const model = data.model;
      const index = data.index;

      this.$bvModal
        .msgBoxConfirm(
          "You are about to remove this item. Do you want to continue?",
          {
            title: "Are you sure?",
            modalClass: ["modal-danger"],
            size: "sm",
            okVariant: "danger",
            okTitle: "YES",
            cancelTitle: "NO",
            footerClass: "p-2",
            centered: true,
          }
        )
        .then(() => {
          this.$bvToast.toast("This will take a second.", {
            title: "Deleting",
            variant: "info",
          });
          model
            .delete()
            .then(() => {
              this.$bvToast.toast("The table will now refresh...", {
                title: "Deleted Successfully",
                variant: "success",
              });
              this.$refs[this.ref].refresh();
              this.$emit("onModelDeleteSuccess", { model, index });
            })
            .catch((err) => {
              console.warn("DELETE FAIL", err);
              this.$bvToast.toast("Please try again later.", {
                title: "Deleted Failed",
                variant: "danger",
              });
              this.$emit("onModelDeleteFailure", { model, index });
            });
        });
    },
    onFetchSuccess(collection) {
      this.$emit("fetchSuccess", collection);
    },
    onFetchError(err) {
      this.$emit("fetchError", err);
      console.warn(err);
    },
  },
};
</script>
